import { faFileExport, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    LinearProgress,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import { green, red } from '@mui/material/colors';
import { action, computed, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import SystemUtil from '@/util/SystemUtil';
import '../../css/cabecalho.css';
import ErrorUtil from '../../util/ErrorUtil';
import NomeUtil from '../../util/NomeUtil';
import ReactComponent from '../ReactComponent';

class ProgressoProcessamentoLote extends ReactComponent {
    constructor(props) {
        super(props);

        makeObservable(this, {
            progressoProcessamentoLote: computed,
            quantidadeErros: computed,
            handleCloseDialogProgresso: action,
            abrirModalProgresso: action,
            existeResultadoProcessamento: computed,
            resultadoProcessamento: computed,
            valorProgressoProcessamentoLote: computed,
            handleClickCheckbox: action,
            isProcessamentoFinalizado: computed,
            textoProgresso: computed,
            textoErros: computed,
            nomeArquivoCsv: computed,
            dadosCsv: computed,
            mensagemErro: computed,
            linarProgressVariant: computed
        });
    }

    get progressoProcessamentoLote() {
        return this.store.progressoProcessamentoLote;
    }

    get quantidadeErros() {
        if (this.existeResultadoProcessamento) {
            return this.progressoProcessamentoLote.resultadoProcessamento.filter((resultado) =>
                this.isResultadoErro(resultado)
            ).length;
        }
        return this.progressoProcessamentoLote?.quantidadeErros ? this.progressoProcessamentoLote.quantidadeErros : 0;
    }

    isResultadoErro(resultado) {
        return ErrorUtil.isResultadoErro(resultado);
    }

    handleCloseDialogProgresso = () => {
        this.store.dialogProgressoProcessamentoLote = false;
    };

    abrirModalProgresso = () => {
        this.store.dialogProgressoProcessamentoLote = true;
    };

    get existeResultadoProcessamento() {
        return this.resultadoProcessamento.length > 0;
    }

    get resultadoProcessamento() {
        if (this.progressoProcessamentoLote?.resultadoProcessamento?.length) {
            return this.progressoProcessamentoLote.resultadoProcessamento
                .slice()
                .sort((r1, r2) => (this.isResultadoErro(r1) && !this.isResultadoErro(r2) ? -1 : 0));
        }
        return [];
    }

    get valorProgressoProcessamentoLote() {
        if (!this.progressoProcessamentoLote) {
            return 0;
        }
        if (this.progressoProcessamentoLote.quantidadeProcessados === this.progressoProcessamentoLote.quantidadeTotal) {
            return 100;
        }
        if (this.progressoProcessamentoLote.quantidadeProcessados > this.progressoProcessamentoLote.quantidadeTotal) {
            console.error(
                `Falha no controle de processamento em lote [${this.progressoProcessamentoLote.descricao}]. O total de registros processados não deveria ser maior do que o total de registros do lote [${this.progressoProcessamentoLote.quantidadeProcessados} de ${this.progressoProcessamentoLote.quantidadeTotal}].`
            );
        }
        return Math.trunc(
            (this.progressoProcessamentoLote.quantidadeProcessados / this.progressoProcessamentoLote.quantidadeTotal) *
                100
        );
    }

    handleClickCheckbox = () => {
        this.store.fecharProgressoAutomaticamente = !this.store.fecharProgressoAutomaticamente;
    };

    get isProcessamentoFinalizado() {
        return this.valorProgressoProcessamentoLote === 100;
    }

    get textoProgresso() {
        let progresso = this.progressoProcessamentoLote;

        if (!progresso) {
            return 'Não há registros em processamento.';
        }
        if (progresso.quantidadeProcessados > progresso.quantidadeTotal) {
            console.error(
                `Falha no controle de processamento em lote [${progresso.descricao}]. O total de registros processados não deveria ser maior do que o total de registros do lote [${progresso.quantidadeProcessados} de ${progresso.quantidadeTotal}].`
            );
        }
        if (progresso.quantidadeProcessados === progresso.quantidadeTotal) {
            let plural =
                progresso.quantidadeProcessados !== 1 ? ' registros foram processados' : ' registro foi processado';
            let sucesso = this.quantidadeErros === 0 ? ' com sucesso' : '';
            return `Operação finalizada. ${progresso.quantidadeTotal}${plural}${sucesso}.`;
        }
        if (progresso.indeterminate) {
            return `Processando ${progresso.quantidadeTotal} registro${progresso.quantidadeTotal !== 1 ? 's' : ''}`;
        }
        return `Processando registro ${progresso.quantidadeProcessados} de ${progresso.quantidadeTotal}. `;
    }

    get textoErros() {
        return this.quantidadeErros > 0 ? this.mensagemErro : null;
    }

    get nomeArquivoCsv() {
        let prefixo = 'lote';

        if (this.progressoProcessamentoLote?.descricao) {
            prefixo = NomeUtil.toCaixaAltaSemAcento(this.progressoProcessamentoLote.descricao.replace(/\s/g, '_'));
        }

        return this.sessaoStore?.montarNomeArquivoExportacao(prefixo);
    }

    get cabecalhoCsv() {
        return [
            { label: 'Identificador', key: 'identificador' },
            { label: 'Resultado', key: 'resultado' }
        ];
    }

    get dadosCsv() {
        return this.resultadoProcessamento.map((resultado) => ({
            identificador: this.getIdentificador(resultado),
            resultado: resultado.mensagem || 'SUCESSO'
        }));
    }

    get mensagemErro() {
        const plural = this.quantidadeErros !== 1 ? 's' : '';
        return (
            <Typography style={{ color: red[900], marginLeft: '4px' }}>
                {`(${this.quantidadeErros} registro${plural} com erro. Verifique os detalhes abaixo.)`}
            </Typography>
        );
    }

    getIdentificador(resultado) {
        if (resultado?.numeroProcesso && resultado?.parte?.nome) {
            return resultado.numeroProcesso + ' ' + resultado.parte.nome;
        }
        if (resultado?.numeroProcesso) {
            return resultado.numeroProcesso;
        }
        if (typeof resultado?.elemento === 'object' && resultado.elemento.numeroProcesso) {
            return resultado.elemento.numeroProcesso;
        }
        if (typeof resultado?.elemento === 'string') {
            return resultado.elemento;
        }
        if (resultado?.identificadorObjeto) {
            if (resultado.identificadorObjeto.numeroProcesso) {
                return resultado.identificadorObjeto.numeroProcesso;
            }
            if (resultado.identificadorObjeto.numeroCompleto) {
                return resultado.identificadorObjeto.numeroCompleto;
            }
        }
    }

    exportarCsv = () => {
        const headers = this.cabecalhoCsv.map((h) => h.label).join(';');
        const rows = this.dadosCsv.map((obj) =>
            this.cabecalhoCsv
                .map((h) => {
                    const value = obj[h.key];
                    return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
                })
                .join(';')
        );
        const csvContent = [headers, ...rows].join('\n');
        SystemUtil.exportarAquivoCsv(this.nomeArquivoCsv, csvContent);
    };

    render() {
        if (!this.progressoProcessamentoLote) {
            return null;
        }

        return (
            <>
                {this.progressoProcessamentoLote.quantidadeTotal > 0 && (
                    <Tooltip
                        title={'Exibir progresso/resultado da última operação'}
                        placement="top-start"
                        className="corBotao"
                    >
                        <Box
                            className="corBotaoAmarelo"
                            onClick={this.abrirModalProgresso}
                            style={{ cursor: 'pointer' }}
                        >
                            <FontAwesomeIcon
                                icon={faSpinner}
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    margin: '8px',
                                    fill: 'white',
                                    paddingTop: '1px'
                                }}
                            />
                        </Box>
                    </Tooltip>
                )}
                {this.store.dialogProgressoProcessamentoLote && (
                    <Dialog
                        fullWidth
                        maxWidth="lg"
                        open={this.store.dialogProgressoProcessamentoLote}
                        onClose={this.handleCloseDialogProgresso}
                    >
                        <DialogTitle>{this.progressoProcessamentoLote.descricao}</DialogTitle>
                        {this.renderDialogContent()}
                        {this.renderDialogActions()}
                    </Dialog>
                )}
            </>
        );
    }

    renderDialogActions() {
        return (
            <DialogActions sx={{ mt: 1, ml: 2, mr: 2, mb: 1, justifyContent: 'space-between' }}>
                <FormGroup row>
                    <FormControlLabel
                        label="Fechar automaticamente após conclusão com sucesso"
                        control={
                            <Checkbox
                                color="primary"
                                onClick={this.handleClickCheckbox}
                                checked={this.store.fecharProgressoAutomaticamente}
                            />
                        }
                    />
                </FormGroup>
                {this.isProcessamentoFinalizado && (
                    <Button
                        color="primary"
                        variant="contained"
                        endIcon={<CloseIcon />}
                        onClick={this.handleCloseDialogProgresso}
                    >
                        Fechar
                    </Button>
                )}
            </DialogActions>
        );
    }

    renderDialogContent() {
        return (
            <DialogContent>
                <Typography variant="subtitle1">
                    {this.textoProgresso}
                    {this.textoErros}
                </Typography>
                <LinearProgress
                    variant={this.linarProgressVariant}
                    value={this.valorProgressoProcessamentoLote}
                    style={{ height: '20px', marginBottom: '20px' }}
                />
                {this.isProcessamentoFinalizado ? this.renderResultadoProcessamento() : this.renderInformacaoOcultar()}
            </DialogContent>
        );
    }

    renderResultadoProcessamento() {
        if (!this.existeResultadoProcessamento) {
            return this.progressoProcessamentoLote.mensagemErro;
        }

        return (
            <Paper
                elevation={5}
                style={{ padding: '16px' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography style={{ fontWeight: 500, marginBottom: '16px' }}>
                        Resultado do processamento
                    </Typography>
                    <Tooltip
                        title={<Typography>Exportar resultado para arquivo CSV</Typography>}
                        placement="top"
                    >
                        <div
                            onClick={this.exportarCsv}
                            style={{ cursor: 'pointer' }}
                        >
                            <FontAwesomeIcon
                                icon={faFileExport}
                                style={{ width: '22px', height: '22px', marginLeft: '16px' }}
                            />
                        </div>
                    </Tooltip>
                </div>
                <div style={{ overflowX: 'auto', minHeight: '60px', maxHeight: '200px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', columnGap: '24px', rowGap: '8px' }}>
                        {this.resultadoProcessamento.map((resultado) => this.renderResultado(resultado))}
                    </div>
                </div>
            </Paper>
        );
    }

    renderResultado(resultado) {
        return (
            <>
                <div>
                    <Typography
                        noWrap
                        sx={{ color: this.isResultadoErro(resultado) ? red[900] : green[900] }}
                    >
                        {this.getIdentificador(resultado)}
                    </Typography>
                </div>
                <div>
                    <Typography sx={{ color: this.isResultadoErro(resultado) ? red[900] : green[900] }}>
                        {this.isResultadoErro(resultado) ? `ERRO: ${resultado.mensagem}` : 'SUCESSO'}
                    </Typography>
                </div>
            </>
        );
    }

    renderInformacaoOcultar() {
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column', fontSize: 'smaller' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    Para abrir novamente a janela de progresso, acione a opção
                    <FontAwesomeIcon
                        icon={faSpinner}
                        style={{ width: '18px', height: '18px', margin: '6px' }}
                    />
                    na barra de ferramentas.
                </div>
            </div>
        );
    }

    get linarProgressVariant() {
        return this.progressoProcessamentoLote.indeterminate && !this.isProcessamentoFinalizado
            ? 'indeterminate'
            : 'determinate';
    }
}

export default inject('stores')(observer(ProgressoProcessamentoLote));
